import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedAsset } from '@app/core/model/entities/asset/asset';
import { Space } from '@app/core/model/entities/asset/space';
import { Equipment } from '@app/core/model/entities/equipments/equipment';
import { Project } from '@app/core/model/entities/project/project';
import { Work, WorkInput } from '@app/core/model/entities/works/work';
import { Field, SECTION_ENTITY_TYPE_INJECTION } from '@app/core/model/other/field-config';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { WorksService } from '@app/features/main/views/works/works.service';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale, isNullOrEmpty } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import { TranslateService } from '@ngx-translate/core';
import { FieldService } from '@services/field.service';
import { AccessManager } from '@services/managers/access.manager';
import { GridApi, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { filter, mergeMap, switchMap, toArray } from 'rxjs/operators';

@Component({
  templateUrl: './work-duplicate-modal.component.html',
  styleUrl: '../../../../../../../shared/components/form-builder/sections/create-modal.component.scss',
  standalone: false,
  providers: [
    {provide: SECTION_ENTITY_TYPE_INJECTION, useValue: EntityTypeEnum.WORK}
  ]
})
export class WorkDuplicateModalComponent implements OnInit, OnDestroy, GeneratesObject {

  // Ag-grid
  public gridApi: GridApi;
  public gridOptions: GridOptions;
  public spaces = new BehaviorSubject<Space[]>([]);

  // Data
  public workStates: Observable<string[]>;

  public appConfig = inject(AppConfig);
  protected readonly EntityTypeEnum = EntityTypeEnum;
  protected modalTitle: string;
  protected requireAsset = false;
  protected allowedEntityIds: string[];
  protected assetTypeAllowsSpaceModule = false;
  protected fb = inject(FormBuilder);
  protected assetsService = inject(AssetsService);
  protected spacesService = inject(SpacesService);
  protected worksService = inject(WorksService);
  protected columnBuilder = inject(ColumnBuilder);
  protected translate: TranslateService = inject(TranslateService);
  protected gridOptionsService = inject(GridOptionsService);
  protected formStateService = inject(FormStateService);
  protected accessManager = inject(AccessManager);
  private fieldService = inject(FieldService);

  protected workForm: FormGroup<{
    action: FormControl<string>,
    state: FormControl<string>,
    asset: FormControl<RelatedAsset>,
    projects: FormControl<Project[]>,
    equipments: FormControl<Equipment[]>,
    spaces: FormControl<Space[]>,
    properties: FormControl<Record<string, any>>
  }>;
  protected missingRequiredFields$: Observable<Field[]>;
  protected destroyRef = inject(DestroyRef);
  private invalidRows: string[] = [];

  constructor() {
    const data = inject<{
      works: Work[];
      project: Project;
      equipment: Equipment;
    }>(MAT_DIALOG_DATA);

    this.modalTitle = data.works.length > 1 ? 'TITLE.DUPLICATE_WORKS' : 'TITLE.DUPLICATE_WORK';
    this.gridOptions = {
      ...this.gridOptionsService.staticSelectSpacesGridOptions,
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      localeText: this.columnBuilder.localeTextCommon(),
      multiSortKey: this.appConfig.GRID_MULTI_SORT_KEY,
      domLayout: 'autoHeight',
      rowClassRules: {
        'invalid-row': ({data}: { data: Space }): boolean => this.invalidRows.includes(data.id)
      },
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this)
    };
    this.workForm = this.fb.group({
      action: this.fb.control(this.translate.instant('VALUE.NEW_WORK'), Validators.compose([
        Validators.required,
        Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)
      ])),
      state: this.fb.control(void 0, Validators.compose([
        Validators.required,
        Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)
      ])),
      asset: this.fb.control(undefined),
      projects: this.fb.control(data.project ? [data.project] : []),
      equipments: this.fb.control(data.equipment ? [data.equipment] : []),
      spaces: this.fb.control([], ExtraValidators.checkSpaces),
      properties: this.fb.control({})
    });
    this.requireAsset = !!data.project || !!data.equipment;
    this.allowedEntityIds = (data.project?.relatedAssets ?? data.equipment?.relatedAssets)?.map(asset => asset.id) ?? [];
    this.workForm.controls.action.setValue(data.works[0].action);
    this.workForm.controls.state.setValue(data.works[0].state);

    // All fields with a value in all selected works
    const worksExistingFields = data.works.flatMap(work =>
      Object.entries(work)
        .filter(([_, value]) => !isNullOrEmpty(value))
        .map(([key]) => key)
        .concat(Object.keys(work.properties))
    );

    this.missingRequiredFields$ = this.fieldService.getFields('ALL', EntityTypeEnum.WORK).pipe(
      mergeMap(fields => from(fields)),
      // Keep fields that exist in all works
      filter(field => (!field.computed || field.hasAlias) && worksExistingFields.includes(field.alias)),
      // Keep remaining fields without sufficient user permissions
      filter(({alias}) => !this.accessManager.hasFieldWriteAccess(alias, EntityTypeEnum.WORK)),
      toArray(),
      takeUntilDestroyed()
    );
  }

  /**
   * Fetch possible values for state and accessible Assets and Spaces.
   */
  public ngOnInit(): void {
    // Fetch available Work states
    this.workStates = this.worksService.loadWorkStates()
      .pipe(takeUntilDestroyed(this.destroyRef));

    // Listen for Spaces to be displayed in the grid
    this.workForm.controls.asset.valueChanges
      .pipe(
        switchMap(asset => {
          this.assetTypeAllowsSpaceModule = asset?.typeModules.includes(EntityTypeEnum.SPACE);
          if (this.assetTypeAllowsSpaceModule) {
            this.workForm.controls.spaces.addValidators(Validators.required);
            const selectParams: SelectParams = {
              locale: getLocale(),
              filters: [eq('assetId', asset.id)]
            };
            return this.spacesService.loadAllSpaces(selectParams);
          }
          this.workForm.controls.spaces.removeValidators(Validators.required);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.spaces);
  }

  /**
   * Stop Observable subscriptions.
   */
  public ngOnDestroy(): void {
    this.spaces.complete();
  }

  /**
   * Load the grid's column definitions and fill with the selected Asset's Spaces.
   * @param params Ag-grid params.
   */
  public onGridReady(params): void {
    this.gridApi = params.api;
    this.gridApi.applyColumnState({
      state: [
        {colId: 'ag-Grid-AutoColumn', sort: 'asc'}
      ]
    });
    // Resize columns when receiving new Spaces
    this.spaces.subscribe(spaces => {
      this.gridApi.setGridOption('rowData', spaces);
      this.gridApi.sizeColumnsToFit();
      this.workForm.controls.spaces.reset([]);
    });
  }

  /**
   * Data from the filled form.
   * @return Work data.
   */
  public getGeneratedObject(): WorkInput {
    const {action, asset, spaces, state, equipments, projects, properties} = this.workForm.getRawValue();
    const spaceIds = spaces.map(space => space.id);
    const equipmentIds = equipments.map(equipment => equipment.id);
    const projectIds = projects.map(project => project.id);
    return {action, assetId: asset?.id, equipmentIds, spaceIds, projectIds, state, properties};
  }

  /**
   * Update value in the form and refresh invalid rows.
   * @param event Event thrown by the grid.
   * @private
   */
  private onRowSelected(event: RowSelectedEvent): void {
    const spaces = this.gridApi.getSelectedRows() as Space[];
    this.workForm.controls.spaces.setValue(spaces);
    this.workForm.controls.spaces.markAsTouched();
    const nodesToRefresh = [event.node];
    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }
    // Refresh rows
    this.gridApi.redrawRows({
      rowNodes: nodesToRefresh
    });
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return True if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => space.parentPath.includes(selectedRow.id));
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return True is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => selectedRow.parentPath.includes(space.id));
  }
}
